/**
 * 報酬個別設定 (commission_sets/{setId}/overrides/{recipientId}) の hooks (v3.2.17)。
 */
import type { CommissionOverride } from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db, functions } from '@/lib/firebase';

export interface CommissionOverrideRecord extends CommissionOverride {
  /** Firestore doc id (= recipientId) */
  id: string;
}

export function useCommissionOverrides(setId: string | null | undefined) {
  return useQuery({
    queryKey: ['commission_sets', setId, 'overrides'],
    enabled: !!setId,
    queryFn: async (): Promise<CommissionOverrideRecord[]> => {
      if (!setId) return [];
      const snap = await getDocs(collection(db, 'commission_sets', setId, 'overrides'));
      return snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as CommissionOverride),
      }));
    },
  });
}

/** 自分専用の override 1 件を直接取得 (SA / 社員 が報酬表で参照する用)。 */
export function useMyCommissionOverride(
  setId: string | null | undefined,
  uid: string | null | undefined,
) {
  return useQuery({
    queryKey: ['commission_sets', setId, 'overrides', uid],
    enabled: !!setId && !!uid,
    queryFn: async (): Promise<CommissionOverride | null> => {
      if (!setId || !uid) return null;
      const snap = await getDoc(doc(db, 'commission_sets', setId, 'overrides', uid));
      return snap.exists() ? (snap.data() as CommissionOverride) : null;
    },
  });
}

// ─── Mutations ───

const upsertFn = httpsCallable<
  {
    setId: string;
    recipientId: string;
    defaultType: 'rate' | 'fixed';
    items: unknown[];
    briefingFee?: number | null;
    /** SA の開拓顧客加算率の既定 (%)。null = セットの標準 / 0 = 加算なし */
    selfDevelopedAdditionRate?: number | null;
    memo?: string | null;
  },
  { ok: true; setId: string; recipientId: string; recipientType: 'sa' | 'employee' }
>(functions, 'upsertCommissionOverride');

export function useUpsertCommissionOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof upsertFn>[0]) => {
      const r = await upsertFn(input);
      return r.data;
    },
    onSuccess: (_d, vars) => {
      if (vars) qc.invalidateQueries({ queryKey: ['commission_sets', vars.setId, 'overrides'] });
    },
  });
}

const deleteFn = httpsCallable<{ setId: string; recipientId: string }, { ok: true }>(
  functions,
  'deleteCommissionOverride',
);

export function useDeleteCommissionOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof deleteFn>[0]) => {
      const r = await deleteFn(input);
      return r.data;
    },
    onSuccess: (_d, vars) => {
      if (vars) qc.invalidateQueries({ queryKey: ['commission_sets', vars.setId, 'overrides'] });
    },
  });
}
